/**
 * Voice Call Card — the receipt a finished call leaves in the chat.
 *
 * The modal is a live view and closes when the call is done. This is what
 * remains: who was called, how long it took, and what the call actually
 * established, sitting in the transcript where it happened. Clicking it
 * reopens the full call — transcript and all — which is fetched on demand
 * rather than carried in chat history.
 */

import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

import type { VoiceCallSummary } from '../../../types/voiceCall';

function formatDuration(seconds?: number | null): string | null {
  if (!seconds) return null;
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function humaniseKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

interface Props {
  summary: VoiceCallSummary;
  onOpen: (callId: string) => void;
}

export default function VoiceCallCard({ summary, onOpen }: Props) {
  const theme = useTheme();
  const failed = summary.status === 'failed' || summary.status === 'timeout';
  const duration = formatDuration(summary.durationSecs);

  // The headline is whatever the call actually established. `objective_met`
  // is a control field, not an answer — it belongs in the outcome line, not
  // in the list of things we learned.
  const facts = Object.entries(summary.extracted).filter(
    ([key]) => key !== 'objective_met',
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(summary.callId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(summary.callId);
        }
      }}
      sx={{
        cursor: 'pointer',
        // The messages area is a flex column, so a child with the default
        // flex-shrink collapses to its own borders when the thread is long —
        // the card renders as a two-pixel line that is still clickable, which
        // is a worse bug than not rendering at all.
        flexShrink: 0,
        border: `1px solid ${theme.tokens.border.subtle}`,
        borderRadius: `${theme.ds.radius.lg}px`,
        bgcolor: theme.tokens.surface.raised,
        overflow: 'hidden',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: theme.tokens.border.strong,
          boxShadow: theme.tokens.shadow.xs,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.tokens.border.focus}`,
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.75,
          py: 1.1,
          borderBottom: facts.length ? `1px solid ${theme.tokens.border.subtle}` : 'none',
        }}
      >
        {failed ? (
          <ErrorOutlineRoundedIcon
            sx={{ fontSize: 17, color: theme.tokens.semantic.danger }}
          />
        ) : (
          <PhoneInTalkRoundedIcon sx={{ fontSize: 17, color: theme.tokens.text.accent }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {failed ? 'Call failed' : 'Called'} {summary.carrierName || summary.phoneNumber}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {duration && (
          <Typography
            variant="caption"
            sx={{ color: theme.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' }}
          >
            {duration}
          </Typography>
        )}
        {summary.policyNumber && (
          <Chip
            size="small"
            variant="outlined"
            label={summary.policyNumber}
            sx={{ fontSize: '0.68rem', height: 20 }}
          />
        )}
      </Stack>

      {facts.length > 0 && (
        <Stack sx={{ px: 1.75, py: 1 }} spacing={0.6}>
          {facts.map(([key, value]) => (
            <Stack
              key={key}
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              spacing={2}
            >
              <Typography variant="body2" sx={{ color: theme.tokens.text.secondary }}>
                {humaniseKey(key)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatValue(value)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 1.75,
          pb: 1.1,
          pt: 0,
          color: theme.tokens.text.tertiary,
        }}
      >
        {failed
          ? 'Open for the reason'
          : summary.turnCount > 0
            ? `Open for the full transcript · ${summary.turnCount} turns`
            : 'Open for details'}
      </Typography>
    </Box>
  );
}
